import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import { EmployeeFilter } from './EmployeeFilter';
import { ShowCompletedToggle } from './ShowCompletedToggle';
import { cn } from '@/shared/utils/utils';

interface GoalsFiltersProps {
  selectedEmployeeId?: string;
  onEmployeeChange: (employeeId: string | undefined) => void;
  showCompleted: boolean;
  onShowCompletedChange: (show: boolean) => void;
  onCollapseAll: () => void;
  className?: string;
}

export const GoalsFilters: React.FC<GoalsFiltersProps> = ({
  selectedEmployeeId,
  onEmployeeChange,
  showCompleted,
  onShowCompletedChange,
  onCollapseAll,
  className,
}) => {
  const { t } = useTranslation('goals');

  // Check if any filters are active
  const hasActiveFilters = selectedEmployeeId !== undefined || !showCompleted;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-2',
            hasActiveFilters && 'border-primary bg-primary/5',
            className
          )}
        >
          <Filter className="h-4 w-4" />
          <span>{t('filters')}</span>
          {hasActiveFilters && (
            <span className="ms-1 flex h-2 w-2 rounded-full bg-primary" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">{t('filters')}</h4>
            <p className="text-xs text-muted-foreground">
              {t('filtersDescription')}
            </p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('filterByEmployee')}
              </label>
              <div className="w-full">
                <EmployeeFilter
                  selectedEmployeeId={selectedEmployeeId}
                  onSelectionChange={onEmployeeChange}
                />
              </div>
            </div>
            <div className="space-y-2">
              <ShowCompletedToggle
                showCompleted={showCompleted}
                onToggle={onShowCompletedChange}
              />
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={onCollapseAll}
            >
              {t('collapseAll')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
